import { useState, useEffect } from 'react';

/**
 * Validation schemas for form data
 */

// Employee validation schema
export const employeeSchema = {
  empcode: {
    required: true,
    minLength: 3,
    maxLength: 10,
    pattern: /^[A-Z0-9]+$/,
    message: 'Employee code must be 3-10 characters, alphanumeric only'
  },
  first_name: {
    required: true,
    minLength: 2,
    maxLength: 50,
    pattern: /^[a-zA-Z\s]+$/,
    message: 'First name must be 2-50 characters, letters only'
  },
  last_name: {
    required: false,
    maxLength: 50,
    pattern: /^[a-zA-Z\s]*$/,
    message: 'Last name must be letters only'
  },
  email_address: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address'
  },
  phone_no: {
    required: true,
    pattern: /^[+]?[\d\s-()]+$/,
    minLength: 10,
    maxLength: 15,
    message: 'Please enter a valid phone number'
  },
  role: {
    required: true,
    enum: ['employee', 'admin', 'manager', 'HR'],
    message: 'Please select a valid role'
  }
};

// Leave request validation schema
export const leaveRequestSchema = {
  leaveType: {
    required: true,
    enum: ['PL', 'SL', 'CL', 'ML', 'PL'],
    message: 'Please select a leave type'
  },
  fromDate: {
    required: true,
    type: 'date',
    validate: (value) => new Date(value) >= new Date(),
    message: 'From date must be today or in the future'
  },
  toDate: {
    required: true,
    type: 'date',
    validate: (value, formData) => new Date(value) >= new Date(formData.fromDate),
    message: 'To date must be after from date'
  },
  days: {
    required: true,
    type: 'number',
    min: 0.5,
    max: 365,
    message: 'Days must be between 0.5 and 365'
  },
  reason: {
    required: true,
    minLength: 10,
    maxLength: 500,
    message: 'Reason must be 10-500 characters'
  }
};

// Attendance rectification schema
export const attendanceRectificationSchema = {
  intime: {
    required: true,
    pattern: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
    message: 'Please enter a valid time (HH:MM)'
  },
  outtime: {
    required: false,
    pattern: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
    message: 'Please enter a valid time (HH:MM)'
  },
  breaktime: {
    required: false,
    pattern: /^([0-9]+):([0-5][0-9])$/,
    message: 'Please enter break time in HH:MM format'
  },
  datestring: {
    required: true,
    type: 'date',
    message: 'Please select a date'
  },
  reason: {
    required: true,
    minLength: 10,
    maxLength: 500,
    message: 'Reason must be 10-500 characters'
  }
};

// Claims validation schema
export const claimSchema = {
  type: {
    required: true,
    enum: ['Travel', 'Meal', 'Internet', 'Medical', 'Office Supplies', 'Training', 'Other'],
    message: 'Please select a claim type'
  },
  amount: {
    required: true,
    type: 'number',
    min: 1,
    max: 100000,
    message: 'Amount must be between 1 and 100,000'
  },
  description: {
    required: true,
    minLength: 10,
    maxLength: 500,
    message: 'Description must be 10-500 characters'
  },
  expenseDate: {
    required: true,
    type: 'date',
    validate: (value) => new Date(value) <= new Date(),
    message: 'Expense date cannot be in the future'
  }
};

// WFH request validation schema
export const wfhRequestSchema = {
  date: {
    required: true,
    type: 'date',
    validate: (value) => new Date(value) >= new Date(),
    message: 'WFH date must be today or in the future'
  },
  reason: {
    required: true,
    minLength: 10,
    maxLength: 500,
    message: 'Reason must be 10-500 characters'
  }
};

// Holiday validation schema
export const holidaySchema = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    message: 'Holiday name must be 2-100 characters'
  },
  date: {
    required: true,
    type: 'date',
    message: 'Please select a date'
  },
  type: {
    required: true,
    enum: ['public', 'optional', 'regional', 'company'],
    message: 'Please select a holiday type'
  },
  description: {
    required: false,
    maxLength: 500,
    message: 'Description must be less than 500 characters'
  }
};

// Team validation schema
export const teamSchema = {
  team_name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    message: 'Team name must be 2-100 characters'
  },
  description: {
    required: false,
    maxLength: 500,
    message: 'Description must be less than 500 characters'
  }
};

/**
 * Generic validation function
 */
export const validateField = (value, rules, formData = {}) => {
  const errors = [];

  // Required validation
  if (rules.required && (!value || value.toString().trim() === '')) {
    errors.push('This field is required');
    return errors;
  }

  // Skip other validations if field is empty and not required
  if (!value || value.toString().trim() === '') {
    return errors;
  }

  // Type validation
  if (rules.type === 'number' && isNaN(Number(value))) {
    errors.push('Must be a valid number');
  }

  if (rules.type === 'date' && isNaN(Date.parse(value))) {
    errors.push('Must be a valid date');
  }

  // Length validation
  if (rules.minLength && value.toString().length < rules.minLength) {
    errors.push(`Must be at least ${rules.minLength} characters`);
  }

  if (rules.maxLength && value.toString().length > rules.maxLength) {
    errors.push(`Must be less than ${rules.maxLength} characters`);
  }

  // Pattern validation
  if (rules.pattern && !rules.pattern.test(value.toString())) {
    errors.push(rules.message || 'Invalid format');
  }

  // Enum validation
  if (rules.enum && !rules.enum.includes(value)) {
    errors.push(`Must be one of: ${rules.enum.join(', ')}`);
  }

  // Min/Max validation for numbers
  if (rules.min !== undefined && Number(value) < rules.min) {
    errors.push(`Must be at least ${rules.min}`);
  }

  if (rules.max !== undefined && Number(value) > rules.max) {
    errors.push(`Must be at most ${rules.max}`);
  }

  // Custom validation
  if (rules.validate && typeof rules.validate === 'function') {
    try {
      const isValid = rules.validate(value, formData);
      if (!isValid) {
        errors.push(rules.message || 'Invalid value');
      }
    } catch (error) {
      errors.push(rules.message || 'Validation error');
    }
  }

  return errors;
};

/**
 * Validate entire form
 */
export const validateForm = (formData, schema) => {
  const errors = {};
  let isValid = true;

  Object.keys(schema).forEach(field => {
    const fieldErrors = validateField(formData[field], schema[field], formData);
    if (fieldErrors.length > 0) {
      errors[field] = fieldErrors;
      isValid = false;
    }
  });

  return { isValid, errors };
};

/**
 * Real-time field validation hook
 */

export const useFieldValidation = (value, rules, formData = {}) => {
  const [errors, setErrors] = useState([]);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (touched) {
      const fieldErrors = validateField(value, rules, formData);
      setErrors(fieldErrors);
    }
  }, [value, rules, formData, touched]);

  const onBlur = () => setTouched(true);
  const clearErrors = () => {
    setErrors([]);
    setTouched(false);
  };

  return {
    errors,
    hasErrors: errors.length > 0,
    onBlur,
    clearErrors,
    touched
  };
};